import React from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

export interface CollapseProps {
  activeKey?: React.Key | React.Key[]
  defaultActiveKey?: React.Key | React.Key[]
  onChange?: (key: React.Key | React.Key[]) => void
  /** false = nhiều panel mở cùng lúc (giống antd Collapse). */
  accordion?: boolean
  bordered?: boolean
  style?: React.CSSProperties
  className?: string
  children?: React.ReactNode
  expandIcon?: (panelProps: { isActive?: boolean }) => React.ReactNode
  [key: string]: any
}

export interface PanelProps {
  header: React.ReactNode
  key: React.Key
  children?: React.ReactNode
  extra?: React.ReactNode
  disabled?: boolean
  showArrow?: boolean
  forceRender?: boolean
  collapsible?: 'header' | 'icon' | 'disabled' | boolean
  style?: React.CSSProperties
  /** Style vùng nội dung panel (dưới header). */
  bodyStyle?: React.CSSProperties
  [key: string]: any
}

const Panel: React.FC<PanelProps> = () => null
Panel.displayName = 'CollapsePanel'

function toKeyArray(k?: React.Key | React.Key[]): string[] {
  if (k == null || k === '') return []
  const arr = Array.isArray(k) ? k : [k]
  return arr.map(String).filter((x) => x !== '')
}

function isPanelElement(child: React.ReactElement): boolean {
  const t = child.type as any
  if (t === Panel) return true
  if (t?.displayName === 'CollapsePanel') return true
  // HMR: module Panel khác instance nhưng vẫn là stub panel
  const props = child.props as PanelProps
  return Boolean(props && 'header' in props && t?.displayName === 'CollapsePanel')
}

function collectPanels(children: React.ReactNode): PanelProps[] {
  const panels: PanelProps[] = []
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return
    if (!isPanelElement(child)) return
    const props = child.props as PanelProps
    panels.push({
      ...props,
      key: child.key ?? props.key,
    })
  })
  return panels
}

export const Collapse: React.FC<CollapseProps> & { Panel: typeof Panel } = ({
  activeKey,
  defaultActiveKey,
  onChange,
  accordion = false,
  style,
  className,
  children,
  expandIcon,
}) => {
  const panels = collectPanels(children)
  const isControlled = activeKey !== undefined
  const [innerKeys, setInnerKeys] = React.useState<string[]>(() => toKeyArray(defaultActiveKey))
  const openKeys = isControlled ? toKeyArray(activeKey) : innerKeys

  const commit = (next: string[]) => {
    if (!isControlled) setInnerKeys(next)
    onChange?.(accordion ? next[0] ?? '' : next)
  }

  const toggle = (key: string) => {
    const isOpen = openKeys.includes(key)
    if (accordion) {
      commit(isOpen ? [] : [key])
      return
    }
    commit(isOpen ? openKeys.filter((k) => k !== key) : [...openKeys, key])
  }

  return (
    <Box className={className} sx={style}>
      {panels.map((panel) => {
        const panelKey = String(panel.key)
        const expanded = openKeys.includes(panelKey)
        const disabled = !!panel.disabled || panel.collapsible === 'disabled'
        const canToggleHeader = !disabled && panel.collapsible !== 'icon'
        const panelStyle = (panel.style || {}) as React.CSSProperties

        return (
          <Box
            key={panelKey}
            sx={{
              bgcolor: panelStyle.backgroundColor ?? '#f7f9fc',
              border: panelStyle.border ?? '1px solid #d8dee6',
              borderRadius: panelStyle.borderRadius ?? '2px',
              mb: panelStyle.marginBottom != null ? undefined : '4px',
              marginBottom: panelStyle.marginBottom,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                minHeight: 32,
                px: 1,
                py: 0.5,
                bgcolor: 'inherit',
                borderBottom: expanded ? '1px solid #e2e8f0' : 'none',
              }}
            >
              {panel.showArrow !== false ? (
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => toggle(panelKey)}
                  aria-expanded={expanded}
                  sx={{
                    p: 0.25,
                    flexShrink: 0,
                    transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.15s ease',
                  }}
                >
                  {expandIcon ? (
                    expandIcon({ isActive: expanded })
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              ) : null}

              <Box
                role={canToggleHeader ? 'button' : undefined}
                tabIndex={canToggleHeader ? 0 : undefined}
                onClick={canToggleHeader ? () => toggle(panelKey) : undefined}
                onKeyDown={
                  canToggleHeader
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          toggle(panelKey)
                        }
                      }
                    : undefined
                }
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  cursor: canToggleHeader ? 'pointer' : 'default',
                  userSelect: 'none',
                }}
              >
                {panel.header}
              </Box>

              {panel.extra ? (
                <Box
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{
                    display: 'inline-flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 0.5,
                    flex: '0 1 auto',
                    maxWidth: '72%',
                  }}
                >
                  {panel.extra}
                </Box>
              ) : null}
            </Box>

            {expanded || panel.forceRender ? (
              <Box
                style={panel.bodyStyle}
                sx={{
                  display: expanded ? 'block' : 'none',
                  px: panel.bodyStyle?.padding != null || panel.bodyStyle?.paddingLeft != null ? undefined : 1,
                  py: panel.bodyStyle?.padding != null || panel.bodyStyle?.paddingTop != null ? undefined : 1,
                  bgcolor: panel.bodyStyle?.backgroundColor ? undefined : '#fff',
                }}
              >
                {panel.children}
              </Box>
            ) : null}
          </Box>
        )
      })}
    </Box>
  )
}

Collapse.Panel = Panel

export default Collapse
